import { useEffect, useMemo, useState } from "react";
import type {
  BusinessGoalPattern,
  CompanyBusinessProfile,
  GeneratedScenarioDraft,
  ScenarioMapping,
  ScenarioOperation
} from "@ai-sales/contracts";
import { api } from "./api.js";
import { CheckIcon, SparklesIcon } from "./icons.js";

const operationLabels: Record<ScenarioOperation, string> = {
  query: "查询",
  aggregate: "统计",
  create: "创建",
  update: "更新",
  delete: "删除"
};

const mappingKindLabels: Record<ScenarioMapping["kind"], string> = {
  object_role: "对象角色",
  field_meaning: "字段含义",
  relationship: "一级关系",
  record_type_or_picklist: "业务状态",
  action: "Action 白名单"
};

const mappingStatusLabels: Record<ScenarioMapping["status"], string> = {
  recommended: "系统推荐",
  confirmed: "已确认",
  excluded: "已排除",
  unresolved: "待补充",
  blocked: "已阻止",
  unsupported: "不支持"
};

const draftStatusLabels: Record<GeneratedScenarioDraft["status"], string> = {
  generated: "候选待确认",
  confirmed: "白名单已确认",
  compiled: "受控版本已生成",
  superseded: "已有更新版本"
};

type MappingChoices = Pick<ScenarioMapping,
  "objectApiName" |
  "fieldApiName" |
  "targetObjectApiName" |
  "recordTypeDeveloperName" |
  "picklistValue" |
  "actionKey" |
  "operation">;

type SavedScenarioDraft = {
  draft: GeneratedScenarioDraft;
  mappings: ScenarioMapping[];
  context?: unknown;
};

export function BusinessGoalCards(props: {
  patterns: BusinessGoalPattern[];
  selectedKeys: string[];
  disabled: boolean;
  onChange: (keys: BusinessGoalPattern["key"][]) => void;
}) {
  return <div className="scenario-pattern-grid">{props.patterns.map((pattern) => {
    const selected = props.selectedKeys.includes(pattern.key);
    return <button
      type="button"
      key={pattern.key}
      disabled={props.disabled}
      className={selected ? "scenario-pattern-card scenario-pattern-card--selected" : "scenario-pattern-card"}
      aria-pressed={selected}
      onClick={() => props.onChange(selected
        ? props.selectedKeys.filter((key) => key !== pattern.key) as BusinessGoalPattern["key"][]
        : [...props.selectedKeys, pattern.key] as BusinessGoalPattern["key"][])}
    >
      <strong>{pattern.label}</strong>
      <span>{pattern.description}</span>
      <small>{pattern.goalHints.slice(0, 2).join(" · ")}</small>
    </button>;
  })}</div>;
}

export function ScenarioContextStep(props: {
  profile: CompanyBusinessProfile;
  patterns: BusinessGoalPattern[];
  disabled: boolean;
  name: string;
  businessGoal: string;
  targetRoles: string;
  exampleUtterances: string;
  selectedPatternKeys: BusinessGoalPattern["key"][];
  requestedOperations: ScenarioOperation[];
  onName: (value: string) => void;
  onBusinessGoal: (value: string) => void;
  onTargetRoles: (value: string) => void;
  onExampleUtterances: (value: string) => void;
  onPatternKeys: (value: BusinessGoalPattern["key"][]) => void;
  onOperations: (value: ScenarioOperation[]) => void;
}) {
  return <div className="scenario-studio-form">
    <div className="scenario-profile-context">
      <strong>使用公司业务档案 v{props.profile.version}</strong>
      <span>{props.profile.companyName} · {props.profile.industry} · {props.profile.productsAndServices.join("、")}</span>
    </div>
    <label>场景名称
      <input value={props.name} disabled={props.disabled} onChange={(event) => props.onName(event.target.value)} placeholder="例如：业务机会跟进" />
    </label>
    <label>业务目标
      <textarea rows={4} value={props.businessGoal} disabled={props.disabled} onChange={(event) => props.onBusinessGoal(event.target.value)} placeholder="用业务语言说明用户希望完成什么。" />
    </label>
    <label>目标角色（每行一个）
      <textarea rows={3} value={props.targetRoles} disabled={props.disabled} onChange={(event) => props.onTargetRoles(event.target.value)} />
    </label>
    <label>用户日常说法（每行一个）
      <textarea rows={4} value={props.exampleUtterances} disabled={props.disabled} onChange={(event) => props.onExampleUtterances(event.target.value)} placeholder="查询最近的业务机会并更新跟进阶段" />
    </label>
    <div><strong className="field-heading">通用业务模式</strong>
      <BusinessGoalCards patterns={props.patterns} selectedKeys={props.selectedPatternKeys} disabled={props.disabled} onChange={props.onPatternKeys} />
      <small className="muted">模式只提供业务目标提示，不固化行业、对象或字段包。</small>
    </div>
    <fieldset className="scenario-operation-picker"><legend>允许生成的操作</legend>
      {(Object.keys(operationLabels) as ScenarioOperation[]).map((operation) => <label key={operation}>
        <input
          type="checkbox"
          disabled={props.disabled}
          checked={props.requestedOperations.includes(operation)}
          onChange={(event) => props.onOperations(event.target.checked
            ? [...new Set([...props.requestedOperations, operation])]
            : props.requestedOperations.filter((value) => value !== operation))}
        />{operationLabels[operation]}
      </label>)}
    </fieldset>
    <div className="scenario-knowledge-deferred">
      <input type="checkbox" disabled aria-label="使用 Salesforce Knowledge" />
      <div><strong>Salesforce Knowledge（暂缓）</strong><span>真实 Knowledge Org、来源、语言和当前用户权限就绪后再开启。</span></div>
    </div>
  </div>;
}

export function ExpertMappingPanel(props: {
  mapping: ScenarioMapping;
  value: MappingChoices;
  disabled: boolean;
  onChange: (next: MappingChoices) => void;
}) {
  if (props.mapping.status !== "unresolved") return null;
  const update = (key: keyof MappingChoices, value: string) => props.onChange({
    ...props.value,
    [key]: value.trim() || null
  });
  return <details className="scenario-expert-panel">
    <summary>专家映射</summary>
    <p>仅在系统无法从已扫描元数据确定含义时填写；保存时仍会对当前 Org 快照做严格校验。</p>
    <div className="scenario-expert-grid">
      <label>对象 API 名称<input disabled={props.disabled} value={props.value.objectApiName ?? ""} onChange={(event) => update("objectApiName", event.target.value)} /></label>
      {props.mapping.kind !== "object_role" ? <label>字段 API 名称<input disabled={props.disabled} value={props.value.fieldApiName ?? ""} onChange={(event) => update("fieldApiName", event.target.value)} /></label> : null}
      {props.mapping.kind === "relationship" ? <label>目标对象 API 名称<input disabled={props.disabled} value={props.value.targetObjectApiName ?? ""} onChange={(event) => update("targetObjectApiName", event.target.value)} /></label> : null}
      {props.mapping.kind === "record_type_or_picklist" ? <>
        <label>Record Type Developer Name<input disabled={props.disabled} value={props.value.recordTypeDeveloperName ?? ""} onChange={(event) => update("recordTypeDeveloperName", event.target.value)} /></label>
        <label>Picklist 值<input disabled={props.disabled} value={props.value.picklistValue ?? ""} onChange={(event) => update("picklistValue", event.target.value)} /></label>
      </> : null}
    </div>
  </details>;
}

export function ScenarioMappingStep(props: {
  mappings: ScenarioMapping[];
  selected: Record<string, boolean>;
  choices: Record<string, MappingChoices>;
  disabled: boolean;
  onSelected: (id: string, selected: boolean) => void;
  onChoices: (id: string, choices: MappingChoices) => void;
}) {
  return <div className="scenario-mapping-list">{props.mappings.map((mapping) => {
    const blocked = mapping.status === "blocked" || mapping.status === "unsupported";
    return <article className={blocked ? "scenario-mapping-card scenario-mapping-card--blocked" : "scenario-mapping-card"} key={mapping.id}>
      <header>
        <label>
          <input
            type="checkbox"
            disabled={props.disabled || blocked}
            checked={Boolean(props.selected[mapping.id])}
            onChange={(event) => props.onSelected(mapping.id, event.target.checked)}
          />
          <span><strong>{mapping.businessMeaning}</strong><small>{mappingKindLabels[mapping.kind]} · 推荐置信度 {Math.round(mapping.confidence * 100)}%</small></span>
        </label>
        <span className={`mapping-status mapping-status--${mapping.status}`}>{mappingStatusLabels[mapping.status]}</span>
      </header>
      <p>{mapping.evidence.join("；")}</p>
      <small>{mapping.impact}</small>
      <ExpertMappingPanel
        mapping={mapping}
        value={props.choices[mapping.id] ?? mappingChoicesFor(mapping)}
        disabled={props.disabled}
        onChange={(choices) => props.onChoices(mapping.id, choices)}
      />
    </article>;
  })}</div>;
}

export function ActionPermissionsStep({ mappings }: { mappings: ScenarioMapping[] }) {
  const actions = mappings.filter(({ kind }) => kind === "action");
  return <section className="scenario-action-review">
    <h3>已选 Action 与确认策略</h3>
    <p>这里只展示将进入场景白名单的动作；创建和更新仍需用户逐次确认。</p>
    {actions.length ? actions.map((mapping) => <div key={mapping.id}>
      <strong>{mapping.actionKey ?? mapping.businessMeaning}</strong>
      <span>{mapping.operation ? operationLabels[mapping.operation] : "待确认"} · {mapping.operation === "create" || mapping.operation === "update" ? "运行时必须预览确认" : "只读执行"}</span>
    </div>) : <small>当前没有选择 Action，场景仅使用已确认的查询与字段能力。</small>}
  </section>;
}

function ScenarioWorkflowProgress({ draft }: { draft?: GeneratedScenarioDraft }) {
  const currentStep = !draft
    ? 1
    : draft.status === "generated" || draft.status === "superseded"
      ? 2
      : draft.status === "confirmed"
        ? 3
        : 4;
  const steps = [
    { number: 1, label: "业务目标与适用范围", complete: Boolean(draft) },
    { number: 2, label: "对象、字段与 Action 白名单", complete: draft?.status === "confirmed" || draft?.status === "compiled" },
    { number: 3, label: "生成受控场景版本", complete: draft?.status === "compiled" },
    { number: 4, label: "自动测试、试用与发布", complete: false }
  ];

  return <ol className="scenario-workflow" aria-label="场景配置进度">
    {steps.map((step) => <li
      key={step.number}
      className={`${step.complete ? "scenario-workflow__step scenario-workflow__step--complete" : "scenario-workflow__step"} ${currentStep === step.number ? "scenario-workflow__step--active" : ""}`}
      aria-current={currentStep === step.number ? "step" : undefined}
    >
      <span>{step.complete ? <CheckIcon size={13} /> : step.number}</span>
      <strong>{step.label}</strong>
    </li>)}
  </ol>;
}

export function ScenarioStudioWizard(props: {
  profile: CompanyBusinessProfile | null;
  canManage: boolean;
  onCompiled?: (scenarioName: string) => void | Promise<void>;
  onContinue?: () => void;
}) {
  const [patterns, setPatterns] = useState<BusinessGoalPattern[]>([]);
  const [name, setName] = useState("");
  const [businessGoal, setBusinessGoal] = useState("");
  const [targetRoles, setTargetRoles] = useState("");
  const [exampleUtterances, setExampleUtterances] = useState("");
  const [selectedPatternKeys, setSelectedPatternKeys] = useState<BusinessGoalPattern["key"][]>([]);
  const [requestedOperations, setRequestedOperations] = useState<ScenarioOperation[]>(["query"]);
  const [draft, setDraft] = useState<GeneratedScenarioDraft>();
  const [mappings, setMappings] = useState<ScenarioMapping[]>([]);
  const [selectedMappings, setSelectedMappings] = useState<Record<string, boolean>>({});
  const [mappingChoices, setMappingChoices] = useState<Record<string, MappingChoices>>({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savedDrafts, setSavedDrafts] = useState<SavedScenarioDraft[]>([]);

  useEffect(() => {
    void api.listScenarioPatterns()
      .then((result) => {
        setPatterns(result);
        setSelectedPatternKeys((current) => current.length ? current : result.slice(0, 1).map(({ key }) => key));
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "业务模式加载失败"));
  }, []);
  useEffect(() => {
    void api.listScenarioDrafts()
      .then((result) => setSavedDrafts(result.filter(({ draft: item }) => item.status !== "compiled" && item.status !== "superseded")))
      .catch(() => setSavedDrafts([]));
  }, []);
  useEffect(() => {
    if (!props.profile || targetRoles.trim()) return;
    setTargetRoles(props.profile.businessRoles.slice(0, 5).join("\n"));
  }, [props.profile?.id]);

  const roles = useMemo(() => uniqueLines(targetRoles), [targetRoles]);
  const examples = useMemo(() => uniqueLines(exampleUtterances), [exampleUtterances]);
  const canGenerate = Boolean(
    props.canManage &&
    props.profile &&
    name.trim() &&
    businessGoal.trim() &&
    roles.length &&
    examples.length &&
    selectedPatternKeys.length &&
    requestedOperations.length
  );
  const contextRequirements = [
    { label: "场景名称", complete: Boolean(name.trim()) },
    { label: "业务目标", complete: Boolean(businessGoal.trim()) },
    { label: "至少一个目标角色", complete: roles.length > 0 },
    { label: "至少一条用户日常说法", complete: examples.length > 0 },
    { label: "业务模式与操作范围", complete: selectedPatternKeys.length > 0 && requestedOperations.length > 0 }
  ];
  const missingRequirementCount = contextRequirements.filter(({ complete }) => !complete).length;
  const requiredMappings = mappings.filter(({ requiresConfirmation }) => requiresConfirmation);
  const actionableRequiredMappings = requiredMappings.filter(({ status }) => status !== "blocked" && status !== "unsupported" && status !== "excluded");
  const allRequiredSelected = actionableRequiredMappings.every(({ id }) => selectedMappings[id]);
  const selectedRequiredCount = actionableRequiredMappings.filter(({ id }) => selectedMappings[id]).length;
  const blockedMappingCount = mappings.filter(({ status }) => status === "blocked" || status === "unsupported").length;
  const selectedActionMappings = mappings.filter((mapping) =>
    mapping.kind === "action" && (selectedMappings[mapping.id] || mapping.status === "confirmed"));

  const run = async (action: string, callback: () => Promise<void>) => {
    setBusy(action);
    setError("");
    setNotice("");
    try {
      await callback();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作失败");
    } finally {
      setBusy("");
    }
  };

  const generate = () => run("generate", async () => {
    const discovery = await api.createScenarioDiscoveryInput({
      name: name.trim(),
      businessGoal: businessGoal.trim(),
      targetRoles: roles,
      exampleUtterances: examples,
      selectedPatternKeys,
      requestedOperations,
      knowledgeRequested: false
    });
    const generated = await api.generateScenarioDraft(discovery.id);
    setDraft(generated.draft);
    setMappings(generated.mappings);
    setSelectedMappings(Object.fromEntries(generated.mappings.map((mapping) => [
      mapping.id,
      mapping.status === "recommended"
    ])));
    setMappingChoices(Object.fromEntries(generated.mappings.map((mapping) => [mapping.id, mappingChoicesFor(mapping)])));
    setNotice(`已生成 ${generated.mappings.length} 项候选映射；系统尚未替你确认任何对象、字段或 Action。`);
  });

  const resumeDraft = (saved: SavedScenarioDraft) => {
    setDraft(saved.draft);
    setMappings(saved.mappings);
    setName(saved.draft.name);
    setTargetRoles(saved.draft.targetRoles.join("\n"));
    setSelectedMappings(Object.fromEntries(saved.mappings.map((mapping) => [
      mapping.id,
      mapping.status === "recommended" || mapping.status === "confirmed"
    ])));
    setMappingChoices(Object.fromEntries(saved.mappings.map((mapping) => [mapping.id, mappingChoicesFor(mapping)])));
    setError("");
    setNotice(`已恢复“${saved.draft.name}”的未完成配置。`);
  };

  const confirm = () => run("confirm", async () => {
    if (!draft) return;
    const result = await api.confirmScenarioMappings(draft.id, mappings.map((mapping) => {
      const choices = mappingChoices[mapping.id] ?? mappingChoicesFor(mapping);
      return {
        id: mapping.id,
        ...choices,
        status: selectedMappings[mapping.id] ? "confirmed" as const : "excluded" as const
      };
    }));
    setDraft(result.draft);
    setMappings(result.mappings);
    setNotice(result.draft.status === "confirmed"
      ? "所有关键映射已确认，可以执行确定性编译。"
      : "确认结果已保存；仍有未解决映射，暂不能编译。");
  });

  const compile = () => run("compile", async () => {
    if (!draft) return;
    const result = await api.compileScenarioDraft(draft.id);
    setDraft(result.draft);
    setMappings((current) => current);
    setNotice(`场景版本 ${result.scenarioVersion.version} 已生成，包含 ${result.actionBindings.length} 个受控 Action。`);
    await props.onCompiled?.(result.scenario.name);
    setSavedDrafts((current) => current.filter(({ draft: item }) => item.id !== draft.id));
  });

  if (!props.profile) return <section className="admin-card scenario-studio-empty">
    <SparklesIcon />
    <div><h2>引导式场景生成</h2><p>先完成上方公司业务档案，系统才能根据真实业务语境提出场景候选。</p></div>
  </section>;

  return <section className="admin-card scenario-studio">
    <header className="scenario-studio__header">
      <div><p className="eyebrow">场景配置向导</p><h2>从业务目标生成受控场景</h2><p>公司档案提供业务语境，Org 快照限定对象和字段；管理员确认后才会形成可测试版本。</p></div>
      {draft ? <span className={draft.status === "compiled" ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>{draftStatusLabels[draft.status]}</span> : null}
    </header>
    <ScenarioWorkflowProgress draft={draft} />
    {error ? <div className="admin-error-banner" role="alert">{error}</div> : null}
    {notice ? <div className="success-banner" role="status" aria-live="polite"><CheckIcon size={17} />{notice}</div> : null}
    {!draft ? <>
      {savedDrafts.length ? <section className="scenario-resume-panel" aria-label="未完成场景草稿">
        <div><strong>继续未完成的场景</strong><p>草稿会保留已确认的对象、字段和 Action；继续后仍会按当前 Org 快照重新校验。</p></div>
        <div>{savedDrafts.slice(0, 3).map((saved) => <button className="button button--secondary" type="button" key={saved.draft.id} disabled={Boolean(busy)} onClick={() => resumeDraft(saved)}>
          继续配置：{saved.draft.name}
        </button>)}</div>
      </section> : null}
      <div className="scenario-stage-heading">
        <span>步骤 1</span>
        <div><h3>说明用户要完成的业务目标</h3><p>使用日常业务语言即可。对象、字段和 Action 会在下一步根据当前 Org 自动提出候选。</p></div>
      </div>
      <ScenarioContextStep
        profile={props.profile}
        patterns={patterns}
        disabled={!props.canManage || Boolean(busy)}
        name={name}
        businessGoal={businessGoal}
        targetRoles={targetRoles}
        exampleUtterances={exampleUtterances}
        selectedPatternKeys={selectedPatternKeys}
        requestedOperations={requestedOperations}
        onName={setName}
        onBusinessGoal={setBusinessGoal}
        onTargetRoles={setTargetRoles}
        onExampleUtterances={setExampleUtterances}
        onPatternKeys={setSelectedPatternKeys}
        onOperations={setRequestedOperations}
      />
      <section className="scenario-readiness" aria-labelledby="scenario-generation-check-title">
        <div><h3 id="scenario-generation-check-title">生成前检查</h3><p>{missingRequirementCount ? `还需完成 ${missingRequirementCount} 项，完成后即可生成候选。` : "生成条件已就绪，下一步不会直接发布或写入 Salesforce。"}</p></div>
        <ul>{contextRequirements.map((item) => <li className={item.complete ? "scenario-readiness__item scenario-readiness__item--complete" : "scenario-readiness__item"} key={item.label}>
          <span>{item.complete ? <CheckIcon size={12} /> : "·"}</span>{item.label}
        </li>)}</ul>
      </section>
      <div className="editor-actions scenario-editor-actions">
        <small>生成后需人工确认对象、字段和 Action 白名单。</small>
        <button className="button button--primary" type="button" disabled={!canGenerate || Boolean(busy)} onClick={() => void generate()}>
          {busy === "generate" ? "正在分析业务语境与 Org…" : "生成场景候选"}
        </button>
      </div>
    </> : <>
      <div className="scenario-stage-heading">
        <span>{draft.status === "compiled" ? "步骤 4" : draft.status === "confirmed" ? "步骤 3" : "步骤 2"}</span>
        <div><h3>{draft.status === "compiled" ? "进入自动测试与试用" : draft.status === "confirmed" ? "生成确定性的受控版本" : "确认对象、字段与 Action 白名单"}</h3>
          <p>{draft.status === "compiled" ? "版本已写入场景工作区，但在自动测试、试用和正式发布前不会对用户生效。" : "系统推荐项默认勾选；被阻止或不支持的能力不会进入场景。"}</p></div>
      </div>
      <div className="scenario-draft-summary">
        <div><span>场景</span><strong>{draft.name}</strong></div>
        <div><span>目标角色</span><strong>{draft.targetRoles.join("、")}</strong></div>
        <div><span>步骤</span><strong>{draft.steps.length}/5</strong></div>
        <div><span>生成方式</span><strong>{draft.generatorMode === "deterministic" ? "确定性候选" : draft.model}</strong></div>
      </div>
      <div className="scenario-mapping-overview" aria-label="映射确认概览">
        <div><span>关键映射</span><strong>{selectedRequiredCount}/{actionableRequiredMappings.length}</strong><small>已选择 / 可确认</small></div>
        <div><span>Action 白名单</span><strong>{selectedActionMappings.length}</strong><small>已选受控动作</small></div>
        <div><span>已阻止</span><strong>{blockedMappingCount}</strong><small>不会进入版本</small></div>
      </div>
      {draft.status !== "compiled" && !allRequiredSelected ? <div className="scenario-attention" role="status">
        仍有关键映射未选择或已被阻止。请逐项核对后再确认白名单。
      </div> : null}
      <ScenarioMappingStep
        mappings={mappings}
        selected={selectedMappings}
        choices={mappingChoices}
        disabled={!props.canManage || Boolean(busy) || draft.status === "compiled"}
        onSelected={(id, selected) => setSelectedMappings((current) => ({ ...current, [id]: selected }))}
        onChoices={(id, choices) => setMappingChoices((current) => ({ ...current, [id]: choices }))}
      />
      <ActionPermissionsStep mappings={selectedActionMappings} />
      <details className="scenario-rules-review">
        <summary>业务规则与测试预览</summary>
        <h3>业务规则</h3>
        <ul>{draft.businessRules.map((rule) => <li key={rule}>{rule}</li>)}</ul>
        <h3>生成测试</h3>
        <ul>{draft.testCases.map((testCase) => <li key={testCase.input}><strong>{testCase.input}</strong><span>{testCase.expectedOutcome}</span></li>)}</ul>
      </details>
      {draft.status === "compiled" ? <section className="scenario-next-step">
        <div><strong>受控场景草稿已进入场景工作区</strong><span>接下来请运行真实模型自动测试；通过后再进入试用、正式发布和用户分配。</span></div>
        {props.onContinue ? <button className="button button--primary" type="button" onClick={props.onContinue}>继续自动测试与试用</button> : null}
      </section> : null}
      <div className="editor-actions scenario-editor-actions">
        <small>{draft.status === "generated" ? "只有确认的映射会进入白名单。" : draft.status === "confirmed" ? "编译只使用已确认的映射，不会调用任意 SOQL、Apex、Flow 或外部 HTTP。" : "需要新目标时可创建另一个场景。"}</small>
        {draft.status !== "compiled" ? <button className="button button--secondary" type="button" disabled={!props.canManage || !allRequiredSelected || Boolean(busy)} onClick={() => void confirm()}>
          {busy === "confirm" ? "正在校验…" : "确认所选映射"}
        </button> : null}
        {draft.status === "confirmed" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void compile()}>
          {busy === "compile" ? "正在编译…" : "生成受控场景版本"}
        </button> : null}
        <button className="text-button" type="button" disabled={Boolean(busy)} onClick={() => {
          setDraft(undefined);
          setMappings([]);
          setSelectedMappings({});
          setMappingChoices({});
          setNotice("");
          setError("");
        }}>{draft.status === "compiled" ? "创建另一个场景" : "返回修改业务目标"}</button>
      </div>
    </>}
  </section>;
}

function uniqueLines(value: string): string[] {
  return [...new Set(value.split("\n").map((item) => item.trim()).filter(Boolean))];
}

function mappingChoicesFor(mapping: ScenarioMapping): MappingChoices {
  return {
    ...(mapping.objectApiName !== undefined ? { objectApiName: mapping.objectApiName } : {}),
    ...(mapping.fieldApiName !== undefined ? { fieldApiName: mapping.fieldApiName } : {}),
    ...(mapping.targetObjectApiName !== undefined ? { targetObjectApiName: mapping.targetObjectApiName } : {}),
    ...(mapping.recordTypeDeveloperName !== undefined ? { recordTypeDeveloperName: mapping.recordTypeDeveloperName } : {}),
    ...(mapping.picklistValue !== undefined ? { picklistValue: mapping.picklistValue } : {}),
    ...(mapping.actionKey !== undefined ? { actionKey: mapping.actionKey } : {}),
    ...(mapping.operation !== undefined ? { operation: mapping.operation } : {})
  };
}
